import React, { useMemo, useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";
import { CaretDownIcon, CaretRightIcon, ShieldCheckIcon, WarningCircleIcon } from "@phosphor-icons/react";

import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import CustomText from "../../../common/general/CustomText/CustomText";
import type { ActiveRubricSetQuery } from "../../../../graphql/generated/graphql";
import RubricCriterionCard from "./RubricCriterionCard";

type RubricSetViewModel = NonNullable<ActiveRubricSetQuery["activeRubricSet"]["data"]>;
type RubricSectionViewModel = RubricSetViewModel["sections"][number];
type RubricCriterionViewModel = RubricSectionViewModel["criteria"][number];

interface RubricReadyProps {
  rubric: RubricSetViewModel;
}

const sortByDisplayOrder = <T extends { displayOrder: number }>(items: T[]) =>
  [...items].sort((left, right) => left.displayOrder - right.displayOrder);

const sumMaxScore = (criteria: RubricCriterionViewModel[]) =>
  criteria.reduce((total, criterion) => total + (criterion.maxScore ?? 0), 0);

const formatSectionMeta = (criteriaCount: number, sectionMaxScore: number, percentage: number) => {
  const standardLabel = criteriaCount === 1 ? "standard" : "standards";
  const scoreText = sectionMaxScore > 0 ? ` - ${sectionMaxScore} pts` : "";
  return `${criteriaCount} ${standardLabel}${scoreText} (${percentage}%)`;
};

const formatCategoryBreakdownSummary = (totalMaxScore: number, totalCriteria: number) => {
  if (totalMaxScore > 0) {
    return `${totalMaxScore} points total`;
  }

  const standardLabel = totalCriteria === 1 ? "standard" : "standards";
  return `${totalCriteria} ${standardLabel}`;
};

function RubricReady({ rubric }: RubricReadyProps) {
  const [expandedSectionIds, setExpandedSectionIds] = useState<Set<string>>(() => new Set());
  const sortedSections = useMemo(() => sortByDisplayOrder(rubric.sections), [rubric.sections]);
  const totalCriteria = useMemo(
    () => rubric.sections.reduce((count, section) => count + section.criteria.length, 0),
    [rubric.sections]
  );
  const totalMaxScore = useMemo(
    () => rubric.sections.reduce((total, section) => total + sumMaxScore(section.criteria), 0),
    [rubric.sections]
  );
  const toggleSection = (sectionId: string) => {
    setExpandedSectionIds((previous) => {
      const next = new Set(previous);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  return (
    <View>
      <View style={styles.activeRubricCard}>
        <View style={styles.activeRubricTitleRow}>
          <ShieldCheckIcon color="#3E68FE" size={18} weight="fill" />
          <View style={styles.activeRubricText}>
            <CustomText
              text={rubric.name}
              size="s"
              weight="bold"
              style={styles.primaryText}
              useTranslationText={false}
            />
            <CustomText
              text={rubric.sourceFile.fileName}
              size="xs"
              style={styles.secondaryText}
              useTranslationText={false}
            />
          </View>
          <View style={styles.readyBadge}>
            <CustomText
              text="Active"
              size="xs"
              weight="bold"
              style={styles.readyBadgeText}
              useTranslationText={false}
            />
          </View>
        </View>
      </View>

      {rubric.parseWarnings.length > 0 ? (
        <View style={styles.warningCallout}>
          <WarningCircleIcon color="#B54708" size={18} weight="bold" />
          <View style={styles.calloutText}>
            <CustomText
              text="Parse warnings"
              size="xs"
              weight="bold"
              style={styles.warningText}
              useTranslationText={false}
            />
            {rubric.parseWarnings.map((warning, warningIndex) => (
              <CustomText
                key={`${warningIndex}-${warning}`}
                text={warning}
                size="xs"
                style={styles.warningText}
                useTranslationText={false}
              />
            ))}
          </View>
        </View>
      ) : null}

      <CustomSpacing type="vertical" size="m" />

      <CustomText
        text={`Category Breakdown - ${formatCategoryBreakdownSummary(totalMaxScore, totalCriteria)}`}
        size="xs"
        weight="bold"
        style={styles.sectionEyebrow}
        useTranslationText={false}
      />
      <CustomSpacing type="vertical" size="s" />

      <View style={styles.sectionList}>
        {sortedSections.map((section) => {
          const isExpanded = expandedSectionIds.has(section.id);
          const sortedCriteria = sortByDisplayOrder(section.criteria);
          const sectionMaxScore = sumMaxScore(sortedCriteria);
          const percentageBasis = totalMaxScore > 0 ? totalMaxScore : totalCriteria;
          const sectionValue = totalMaxScore > 0 ? sectionMaxScore : sortedCriteria.length;
          const sectionPercentage = percentageBasis > 0 ? Math.round((sectionValue / percentageBasis) * 100) : 0;

          return (
            <View key={section.id} style={styles.sectionCard}>
              <Pressable
                role="button"
                accessibilityLabel={`${isExpanded ? "Collapse" : "Expand"} ${section.title}`}
                onPress={() => toggleSection(section.id)}
                style={[styles.sectionHeader, isExpanded ? styles.sectionHeaderExpanded : null]}>
                <View style={styles.sectionCaret}>
                  {isExpanded ? (
                    <CaretDownIcon color="#667085" size={16} weight="bold" />
                  ) : (
                    <CaretRightIcon color="#667085" size={16} weight="bold" />
                  )}
                </View>
                <View style={styles.sectionHeaderText}>
                  <View style={styles.sectionTitleRow}>
                    <CustomText
                      text={section.title}
                      size="s"
                      weight="medium"
                      style={styles.sectionTitle}
                      useTranslationText={false}
                    />
                    <CustomText
                      text={formatSectionMeta(sortedCriteria.length, sectionMaxScore, sectionPercentage)}
                      size="xs"
                      style={styles.sectionMetaText}
                      useTranslationText={false}
                    />
                  </View>
                  {section.description ? (
                    <CustomText
                      text={section.description}
                      size="xs"
                      style={styles.secondaryText}
                      useTranslationText={false}
                      numberOfLines={1}
                    />
                  ) : null}
                  <View style={styles.sectionProgressTrack}>
                    <View style={[styles.sectionProgressFill, { width: `${sectionPercentage}%` }]} />
                  </View>
                </View>
              </Pressable>
              {isExpanded ? (
                <View style={styles.criteriaList}>
                  {sortedCriteria.length > 0 ? (
                    sortedCriteria.map((criterion) => <RubricCriterionCard key={criterion.id} criterion={criterion} />)
                  ) : (
                    <CustomText
                      text="No criteria were extracted for this section"
                      size="xs"
                      style={styles.secondaryText}
                      useTranslationText={false}
                    />
                  )}
                </View>
              ) : null}
            </View>
          );
        })}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  primaryText: {
    color: "#101828",
  },
  secondaryText: {
    color: "#344054",
  },
  warningText: {
    color: "#B54708",
  },
  calloutText: {
    flex: 1,
    gap: 4,
  },
  warningCallout: {
    marginTop: 16,
    flexDirection: "row",
    gap: 10,
    padding: 12,
    borderRadius: 8,
    backgroundColor: "#FFFAEB",
    borderWidth: 1,
    borderColor: "#FEDF89",
  },
  activeRubricCard: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: "#D1E0FF",
    backgroundColor: "#F5F8FF",
    borderRadius: 8,
  },
  activeRubricTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  activeRubricText: {
    flex: 1,
  },
  readyBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
    backgroundColor: "#E3FBCC",
  },
  readyBadgeText: {
    color: "#2B5314",
  },
  sectionEyebrow: {
    color: "#344054",
  },
  sectionList: {
    gap: 0,
    borderTopWidth: 1,
    borderTopColor: "#F2F4F7",
  },
  sectionCard: {
    borderBottomWidth: 1,
    borderBottomColor: "#F2F4F7",
    backgroundColor: "#FFFFFF",
    overflow: "hidden",
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "stretch",
    gap: 12,
    paddingHorizontal: 0,
    paddingVertical: 12,
  },
  sectionCaret: {
    alignItems: "center",
    justifyContent: "center",
    minHeight: 48,
  },
  sectionHeaderExpanded: {
    marginBottom: 8,
  },
  sectionHeaderText: {
    flex: 1,
    gap: 4,
    minWidth: 0,
  },
  sectionTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  sectionTitle: {
    color: "#101828",
    flex: 1,
  },
  sectionMetaText: {
    color: "#667085",
    flexShrink: 0,
  },
  sectionProgressTrack: {
    height: 6,
    borderRadius: 8,
    backgroundColor: "#F2F4F7",
    overflow: "hidden",
    marginTop: 4,
  },
  sectionProgressFill: {
    height: "100%",
    borderRadius: 8,
    backgroundColor: "#3E68FE",
  },
  criteriaList: {
    gap: 8,
    paddingLeft: 28,
    paddingRight: 0,
    paddingBottom: 12,
  },
});

export default RubricReady;
